import { useCallback, useRef } from 'react';
import { reactHookForm } from '@yola/ws-ui';

const { useForm } = reactHookForm;

const useFormValidation = (initialData = null) => {
  const chunkToFieldsMapping = useRef(initialData?.chunkToFieldsMapping || new Map());

  const {
    control,
    formState,
    getValues,
    setValue,
    setFocus,
    reset,
    trigger,
    unregister,
    clearErrors,
  } = useForm({
    mode: 'onTouched',
    defaultValues: initialData?.defaultValues || {},
    values: initialData?.values || {},
    shouldUnregister: false,
  });

  const triggerFormValidation = useCallback(async () => {
    if (Object.keys(getValues()).length === 0) {
      return Promise.resolve();
    }

    const isValid = await trigger();

    if (!isValid) {
      const error = new Error('Validation failed');
      error.errors = formState.errors;
      return Promise.reject(error);
    }

    return Promise.resolve();
  }, [trigger, formState, getValues]);

  const triggerFieldValidation = useCallback(
    (fieldName, options) => trigger(fieldName, options),
    [trigger]
  );

  const resetFields = useCallback(
    (
      fields,
      { keepErrors = false, keepDirty = false, keepValues = true, keepDefaultValues = true } = {}
    ) => {
      const newValues = Object.entries(getValues())
        .filter(([fieldName]) => !fields.includes(fieldName))
        .reduce(
          (acc, [fieldName, value]) => ({
            ...acc,
            [fieldName]: value,
          }),
          {}
        );

      reset(newValues, { keepErrors, keepDirty, keepValues, keepDefaultValues });
    },
    [reset, getValues]
  );

  const restoreFields = useCallback(
    (fields) => {
      fields.forEach((fieldName) => {
        const value = getValues(fieldName);
        setValue(fieldName, value || '', {
          shouldTouch: true,
          shouldValidate: false,
        });
      });
    },
    [getValues, setValue]
  );

  const manageFields = useCallback(
    async ({ deactivate = [], activate = [] }) => {
      if (deactivate.length > 0) {
        resetFields(deactivate);
      }

      // Wait one animation frame so that React Hook Form has time to re-render and re-register any conditional fields after reset().
      // Without this, calling trigger() or setValue() might run too early, before the new input is actually mounted and registered.
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (activate.length > 0) {
        restoreFields(activate);
      }
    },
    [resetFields, restoreFields]
  );

  const registerDefaultValues = useCallback(
    (chunkId, values) => {
      const currentValues = getValues();
      const hasConflicts = Object.keys(values).some((key) => key in currentValues);

      if (hasConflicts) {
        // eslint-disable-next-line no-console
        console.warn('Field name conflicts detected - some values may be overwritten');
      }

      chunkToFieldsMapping.current.set(chunkId, new Set(Object.keys(values)));

      reset(
        { ...currentValues, ...values },
        {
          keepDefaultValues: false,
          keepErrors: false,
          keepDirty: true,
        }
      );
    },
    [reset, getValues]
  );

  const unregisterDefaultValues = useCallback(
    (chunkId) => {
      const fieldsToUnregister = chunkToFieldsMapping.current.get(chunkId);

      if (!fieldsToUnregister?.size) {
        return;
      }

      Array.from(fieldsToUnregister).forEach((fieldName) => {
        unregister(fieldName);
      });

      chunkToFieldsMapping.current.delete(chunkId);
    },
    [unregister]
  );

  const getDefaultValues = useCallback(() => formState.defaultValues || {}, [formState]);

  const getChunkIdByFieldName = useCallback((fieldName) => {
    const chunkId = Array.from(chunkToFieldsMapping.current.keys()).find((id) =>
      chunkToFieldsMapping.current.get(id).has(fieldName)
    );

    return chunkId || null;
  }, []);

  const getPersistData = useCallback(
    () => ({
      chunkToFieldsMapping: chunkToFieldsMapping.current,
      defaultValues: formState.defaultValues || {},
      values: getValues(),
    }),
    [formState.defaultValues, getValues]
  );

  return {
    control,
    triggerFormValidation,
    triggerFieldValidation,
    registerDefaultValues,
    unregisterDefaultValues,
    getDefaultValues,
    getValues,
    getChunkIdByFieldName,
    getPersistData,
    setValue,
    setFocus,
    clearErrors,
    manageFields,
  };
};

export default useFormValidation;
